import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import XLSX from 'sheetjs-style';
import FileSaver from 'file-saver';
import './MultiProjectView.css';

const MultiProjectView = ({ data }) => {
  console.log("MultiProjectView received data:", data?.length, "rows");
  const [multiProjectResources, setMultiProjectResources] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [filterType, setFilterType] = useState('all'); // all, billable, nonbillable, shadow
  const [sortBy, setSortBy] = useState('projectCount'); // projectCount, employeeId, resourceName, billAllocation, costAllocation, totalAllocation
  const [searchTerm, setSearchTerm] = useState('');

  // Helper functions to extract data from rows
  const findProjectName = (row) => {
    const projectFields = ['projectName', 'Project Name', 'ProjectName', 'Project', 'project', 'Project_Name'];
    for (const field of projectFields) {
      if (row[field]) return String(row[field]).trim();
    }
    return 'Unknown Project';
  };

  const findEmployeeId = (row) => {
    const idFields = ['zohoEmployeeId', 'employeeId', 'Employee ID', 'EmployeeId', 'empId', 'emp_id', 'employee_id', 'id', 'Id', 'ID', 'employeeID', 'EmployeeID'];
    for (const field of idFields) {
      if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
        return String(row[field]).trim();
      }
    }
    
    // Fallback: try to create a unique identifier from resource name and other fields
    const resourceName = findResourceName(row);
    if (resourceName !== 'Unknown Resource') {
      // Create a pseudo-ID from resource name (remove spaces, lowercase)
      return resourceName.toLowerCase().replace(/\s+/g, '_');
    }
    
    return 'Unknown ID';
  };

  const findResourceName = (row) => {
    const nameFields = ['employeeName', 'Resource Name', 'Name', 'Employee Name', 'Full Name', 'Employee', 'Resource'];
    for (const field of nameFields) {
      if (row[field]) return String(row[field]).trim();
    }
    return 'Unknown Resource';
  };

  const findBillStatus = (row) => {
    const billStatusFields = ['billStatus', 'Bill Status', 'BillStatus', 'Billable Status', 'Status', 'billable_status', 'bill_status', 'Type', 'Resource Type', 'Category'];
    for (const field of billStatusFields) {
      if (row[field] && String(row[field]).trim() !== '') {
        const value = String(row[field]).trim();
        return value;
      }
    }
    
    for (const key of Object.keys(row)) {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('billable') || keyLower.includes('status') || keyLower.includes('type')) {
        if (row[key] && String(row[key]).trim() !== '') {
          return String(row[key]).trim();
        }
      }
    }
    
    return 'unknown';
  };

  const findBillAllocation = (row) => {
    const billAllocationFields = ['billAllocation', 'Bill Allocation', 'BillAllocation', 'Billing Allocation', 'bill_allocation'];
    for (const field of billAllocationFields) {
      if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
        const value = parseFloat(row[field]);
        if (!isNaN(value)) {
          return value >= 1 ? value : value * 100; // Convert to percentage (only multiply if decimal < 1)
        }
      }
    }
    return 50; // Default 50%
  };

  const findCostAllocation = (row) => {
    const costAllocationFields = ['costAllocation', 'Cost Allocation', 'CostAllocation', 'cost_allocation'];
    for (const field of costAllocationFields) {
      if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
        const value = parseFloat(row[field]);
        if (!isNaN(value)) {
          return value >= 1 ? value : value * 100; // Convert to percentage (only multiply if decimal < 1)
        }
      }
    }
    return 50; // Default 50%
  };

  const findAllocation = (row) => {
    const allocationFields = ['billAllocation', 'costAllocation', 'Allocation', 'allocation', 'Allocation %', 'Alloc', 'Percent Allocation', 'Percentage', '%', 'Rate', 'Utilization'];
    for (const field of allocationFields) {
      if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
        const value = parseFloat(row[field]);
        if (!isNaN(value)) {
          return value >= 1 ? value : value * 100; // Convert to percentage (only multiply if decimal < 1)
        }
      }
    }
    
    for (const key of Object.keys(row)) {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('alloc') || keyLower.includes('percent') || keyLower.includes('%') || keyLower.includes('rate')) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
          const value = parseFloat(row[key]);
          if (!isNaN(value)) {
            return value >= 1 ? value : value * 100; // Convert to percentage (only multiply if decimal < 1)
          }
        }
      }
    }
    
    return 50; // Default 50%
  };

  const normalizeBillStatus = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('billable') && !statusLower.includes('non')) {
      return 'Billable';
    }
    if (statusLower.includes('non') || statusLower.includes('unbillable') || statusLower.startsWith('nb-') || statusLower.startsWith('nb ')) {
      return 'Non-Billable';
    }
    if (statusLower.includes('shadow')) {
      return 'Shadow';
    }
    return status; // Keep original if no pattern matches
  };

  // Process data to find resources with multiple projects
  useEffect(() => {
    if (!data || data.length === 0) return;

    const resourceProjects = {};
    
    // Group data by employeeId
    data.forEach((row, index) => {
      const employeeId = findEmployeeId(row);
      const resourceName = findResourceName(row);
      const projectName = findProjectName(row);
      const billStatus = normalizeBillStatus(findBillStatus(row));
      const billAllocation = findBillAllocation(row);
      const costAllocation = findCostAllocation(row);
      const allocation = findAllocation(row);
      
      // Skip bench projects and unknown resources
      if (projectName.toLowerCase().includes('bench') || 
          projectName.toLowerCase().includes('unallocated') ||
          employeeId === 'Unknown ID' ||
          resourceName === 'Unknown Resource' ||
          projectName === 'Unknown Project') {
        return;
      }
      
      if (!resourceProjects[employeeId]) {
        resourceProjects[employeeId] = {
          resourceName: resourceName, // Store the resource name for display
          projects: {}
        };
      }
      
      if (!resourceProjects[employeeId].projects[projectName]) {
        resourceProjects[employeeId].projects[projectName] = {
          billStatuses: [],
          totalBillAllocation: 0,
          totalCostAllocation: 0,
          totalAllocation: 0,
          projectName: projectName
        };
      }
      
      resourceProjects[employeeId].projects[projectName].billStatuses.push({
        status: billStatus,
        billAllocation: billAllocation,
        costAllocation: costAllocation,
        allocation: allocation
      });
      
      // Only add to bill allocation if status is 'Billable'
      if (billStatus === 'Billable') {
        resourceProjects[employeeId].projects[projectName].totalBillAllocation += billAllocation;
      }
      resourceProjects[employeeId].projects[projectName].totalCostAllocation += costAllocation;
      resourceProjects[employeeId].projects[projectName].totalAllocation += allocation;
    });

    // Filter resources with multiple projects
    const multiProjectList = [];
    const summary = {
      totalMultiProjectResources: 0,
      billableOnly: 0,
      nonBillableOnly: 0,
      shadowOnly: 0,
      mixed: 0,
      averageProjects: 0,
      maxProjects: 0
    };

    Object.keys(resourceProjects).forEach(employeeId => {
      const resourceData = resourceProjects[employeeId];
      const projects = Object.keys(resourceData.projects);
      
      if (projects.length > 1) {
        const resource = {
          employeeId,
          resourceName: resourceData.resourceName,
          projectCount: projects.length,
          projects: [],
          totalBillAllocation: 0,
          totalCostAllocation: 0,
          totalAllocation: 0,
          billStatusTypes: new Set(),
          hasMultipleBillStatuses: false
        };

        projects.forEach(projectName => {
          const projectData = resourceData.projects[projectName];
          const uniqueStatuses = [...new Set(projectData.billStatuses.map(b => b.status))];
          
          // Calculate billable allocation only for this project
          const projectBillableAllocation = projectData.billStatuses
            .filter(b => b.status === 'Billable')
            .reduce((sum, b) => sum + b.billAllocation, 0);
          
          resource.projects.push({
            projectName,
            billStatuses: uniqueStatuses,
            totalBillAllocation: projectBillableAllocation,
            totalCostAllocation: projectData.totalCostAllocation,
            totalAllocation: projectData.totalAllocation,
            allocations: projectData.billStatuses
          });
          
          resource.totalBillAllocation += projectBillableAllocation;
          resource.totalCostAllocation += projectData.totalCostAllocation;
          resource.totalAllocation += projectData.totalAllocation;
          uniqueStatuses.forEach(status => resource.billStatusTypes.add(status));
        });

        resource.hasMultipleBillStatuses = resource.billStatusTypes.size > 1;
        resource.billStatusSummary = Array.from(resource.billStatusTypes).join(', ');
        
        multiProjectList.push(resource);
        
        // Update summary
        summary.totalMultiProjectResources++;
        summary.averageProjects += resource.projectCount;
        summary.maxProjects = Math.max(summary.maxProjects, resource.projectCount);
        
        if (resource.billStatusTypes.size === 1) {
          const singleStatus = Array.from(resource.billStatusTypes)[0];
          if (singleStatus === 'Billable') summary.billableOnly++;
          else if (singleStatus === 'Non-Billable') summary.nonBillableOnly++;
          else if (singleStatus === 'Shadow') summary.shadowOnly++;
        } else {
          summary.mixed++;
        }
      }
    });

    summary.averageProjects = summary.totalMultiProjectResources > 0 
      ? (summary.averageProjects / summary.totalMultiProjectResources).toFixed(1) 
      : 0;

    // Sort the list
    multiProjectList.sort((a, b) => {
      if (sortBy === 'projectCount') {
        return b.projectCount - a.projectCount;
      } else if (sortBy === 'employeeId') {
        return a.employeeId.localeCompare(b.employeeId);
      } else if (sortBy === 'resourceName') {
        return a.resourceName.localeCompare(b.resourceName);
      } else if (sortBy === 'billAllocation') {
        return b.totalBillAllocation - a.totalBillAllocation;
      } else if (sortBy === 'costAllocation') {
        return b.totalCostAllocation - a.totalCostAllocation;
      } else if (sortBy === 'totalAllocation') {
        return b.totalAllocation - a.totalAllocation;
      } else {
        return a.resourceName.localeCompare(b.resourceName);
      }
    });

    console.log('Multi-project analysis:', {
      totalEmployees: Object.keys(resourceProjects).length,
      multiProjectResources: multiProjectList.length,
      summary
    });

    setMultiProjectResources(multiProjectList);
    setSummaryData(summary);
  }, [data, sortBy]);

  // Filter resources based on selected filter and search term
  const filteredResources = multiProjectResources.filter(resource => {
    // Apply billing status filter
    let statusMatch = true;
    if (filterType === 'billable') statusMatch = resource.billStatusTypes.has('Billable') && resource.billStatusTypes.size === 1;
    else if (filterType === 'nonbillable') statusMatch = resource.billStatusTypes.has('Non-Billable') && resource.billStatusTypes.size === 1;
    else if (filterType === 'shadow') statusMatch = resource.billStatusTypes.has('Shadow') && resource.billStatusTypes.size === 1;
    else if (filterType === 'mixed') statusMatch = resource.billStatusTypes.size > 1;
    
    // Apply search filter
    const searchMatch = searchTerm === '' || 
      resource.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.resourceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.projects.some(project => 
        project.projectName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    return statusMatch && searchMatch;
  });

  // Export to Excel function
  const exportToExcel = () => {
    if (filteredResources.length === 0) {
      return;
    }

    // Prepare flattened data for Excel (one row per project allocation)
    const excelData = [];
    
    filteredResources.forEach(resource => {
      resource.projects.forEach(project => {
        excelData.push({
          'Employee ID': resource.employeeId,
          'Resource Name': resource.resourceName,
          'Project Count': resource.projectCount,
          'Project Name': project.projectName,
          'Bill Statuses': project.billStatuses.join(', '),
          'Bill Allocation (%)': project.totalBillAllocation.toFixed(0),
          'Cost Allocation (%)': project.totalCostAllocation.toFixed(0),
          'Total Allocation (%)': project.totalAllocation.toFixed(0),
          'Total Bill Allocation (%)': resource.totalBillAllocation.toFixed(0),
          'Total Cost Allocation (%)': resource.totalCostAllocation.toFixed(0),
          'Total Resource Allocation (%)': resource.totalAllocation.toFixed(0),
          'Status': resource.hasMultipleBillStatuses ? 'Mixed' : 'Single'
        });
      });
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Employee ID
      { wch: 25 }, // Resource Name
      { wch: 12 }, // Project Count
      { wch: 40 }, // Project Name
      { wch: 20 }, // Bill Statuses
      { wch: 18 }, // Bill Allocation
      { wch: 18 }, // Cost Allocation
      { wch: 18 }, // Total Allocation
      { wch: 22 }, // Total Bill Allocation
      { wch: 22 }, // Total Cost Allocation
      { wch: 25 }, // Total Resource Allocation
      { wch: 10 }  // Status
    ];

    // Style header row
    const headerCellStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1773BC" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true }
    };

    // Apply header styles
    const headers = Object.keys(excelData[0] || {});
    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      if (ws[cellRef]) {
        ws[cellRef].s = headerCellStyle;
      }
    });

    // Create workbook
    const wb = { Sheets: { 'Multi-Project Resources': ws }, SheetNames: ['Multi-Project Resources'] };

    // Generate Excel file
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    
    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const fileName = `Multi-Project-Resources-Report-${date}.xlsx`;
    
    FileSaver.saveAs(data, fileName);
  };

  // Prepare chart data
  const chartData = [
    { name: 'Billable Only', count: summaryData.billableOnly, fill: '#4CAF50' },
    { name: 'Non-Billable Only', count: summaryData.nonBillableOnly, fill: '#FF9800' },
    { name: 'Shadow Only', count: summaryData.shadowOnly, fill: '#9C27B0' },
    { name: 'Mixed Statuses', count: summaryData.mixed, fill: '#F44336' }
  ];

  const projectCountDistribution = {};
  multiProjectResources.forEach(resource => {
    const count = resource.projectCount;
    projectCountDistribution[count] = (projectCountDistribution[count] || 0) + 1;
  });

  const distributionData = Object.keys(projectCountDistribution).map(count => ({
    projectCount: `${count} Projects`,
    resources: projectCountDistribution[count]
  }));

  // Show loading state if no data
  if (!data || data.length === 0) {
    return (
      <div className="multi-project-view">
        <div className="view-header">
          <h1>Multi-Projects Allocation Report</h1>
          <p>Resources assigned to multiple projects with different billing statuses</p>
        </div>
        <div className="empty-state">
          <h3>📊 No data available</h3>
          <p>
            {!data ? "Loading project allocation data..." : "No project allocation data found."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="multi-project-view">
      <div className="view-header">
        <h1>Multi-Projects Allocation Report</h1>
        <p>Resources assigned to multiple projects with different billing statuses</p>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card primary">
          <div className="card-icon">👥</div>
          <div className="card-content">
            <h3>{summaryData.totalMultiProjectResources}</h3>
            <p>Total Multi-Projects Allocation Resources</p>
          </div>
        </div>
        
        <div className="summary-card warning">
          <div className="card-icon">⚠️</div>
          <div className="card-content">
            <h3>{summaryData.mixed}</h3>
            <p>Mixed Billing Statuses</p>
          </div>
        </div>
        
        <div className="summary-card info">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>{summaryData.averageProjects}</h3>
            <p>Average Projects per Resource</p>
          </div>
        </div>
        
        <div className="summary-card success">
          <div className="card-icon">🎯</div>
          <div className="card-content">
            <h3>{summaryData.maxProjects}</h3>
            <p>Maximum Projects</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3>Billing Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, count, percent }) => `${name}: ${count} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Project Count Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={distributionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="projectCount" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="resources" fill="#2196F3" name="Number of Resources" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="controls">
        <div className="filter-group search-group">
          <label>Search Resources:</label>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by employee ID, resource name, or project name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>Filter by Billing Status:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Resources</option>
            <option value="billable">Billable Only</option>
            <option value="nonbillable">Non-Billable Only</option>
            <option value="shadow">Shadow Only</option>
            <option value="mixed">Mixed Statuses</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="projectCount">Project Count</option>
            <option value="employeeId">Employee ID</option>
            <option value="resourceName">Resource Name</option>
            <option value="billAllocation">Bill Allocation (High to Low)</option>
            <option value="costAllocation">Cost Allocation (High to Low)</option>
            <option value="totalAllocation">Total Allocation (High to Low)</option>
          </select>
        </div>
      </div>

      {/* Resource Table */}
      <div className="table-container">
        <div className="table-header">
          <div className="table-header-left">
            <h3>Multi-Project Resources ({filteredResources.length})</h3>
            {searchTerm && (
              <div className="search-results-info">
                <span className="search-indicator">🔍</span>
                <span>Showing results for "{searchTerm}"</span>
                {filteredResources.length !== multiProjectResources.length && (
                  <span className="results-count">
                    ({filteredResources.length} of {multiProjectResources.length})
                  </span>
                )}
              </div>
            )}
          </div>
          <button 
            className="download-excel-btn"
            onClick={exportToExcel}
            disabled={filteredResources.length === 0}
            title="Download Excel Report"
          >
            <span className="download-icon">📥</span>
            <span>Download Excel</span>
          </button>
        </div>
        {filteredResources.length > 0 ? (
          <div className="table-wrapper">
            <table className="multi-project-table">
              <thead>
                <tr>
                  <th 
                    className="sortable-header" 
                    onClick={() => setSortBy('employeeId')}
                    title="Click to sort by Employee ID"
                  >
                    Employee ID {sortBy === 'employeeId' ? '↑' : ''}
                  </th>
                  <th 
                    className="sortable-header" 
                    onClick={() => setSortBy('resourceName')}
                    title="Click to sort by Resource Name"
                  >
                    Resource Name {sortBy === 'resourceName' ? '↑' : ''}
                  </th>
                  <th 
                    className="sortable-header" 
                    onClick={() => setSortBy('projectCount')}
                    title="Click to sort by Project Count"
                  >
                    Project Count {sortBy === 'projectCount' ? '↓' : ''}
                  </th>
                  <th>Projects</th>
                  <th>Billing Statuses</th>
                  <th 
                    className="sortable-header" 
                    onClick={() => setSortBy('billAllocation')}
                    title="Click to sort by Bill Allocation"
                  >
                    Bill Allocation {sortBy === 'billAllocation' ? '↓' : ''}
                  </th>
                  <th 
                    className="sortable-header" 
                    onClick={() => setSortBy('costAllocation')}
                    title="Click to sort by Cost Allocation"
                  >
                    Cost Allocation {sortBy === 'costAllocation' ? '↓' : ''}
                  </th>
                  <th 
                    className="sortable-header" 
                    onClick={() => setSortBy('totalAllocation')}
                    title="Click to sort by Total Allocation"
                  >
                    Total Allocation {sortBy === 'totalAllocation' ? '↓' : ''}
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredResources.map((resource, index) => (
                  <tr key={index} className={resource.hasMultipleBillStatuses ? 'mixed-status' : ''}>
                    <td className="employee-id">
                      <strong>{resource.employeeId}</strong>
                    </td>
                    <td className="resource-name">
                      <strong>{resource.resourceName}</strong>
                    </td>
                    <td className="project-count">
                      <span className="count-badge">{resource.projectCount}</span>
                    </td>
                    <td className="projects-list">
                      <div className="projects-container">
                        {resource.projects.map((project, pIndex) => (
                          <div key={pIndex} className="project-item">
                            <span className="project-name">{project.projectName}</span>
                            <div className="project-details">
                              <div className="allocation-details">
                                <span className="bill-allocation">Allocation: {project.totalAllocation.toFixed(0)}%</span>
                                <span className="cost-allocation">Cost: {project.totalCostAllocation.toFixed(0)}%</span>
                              </div>
                              <div className="bill-statuses">
                                {project.billStatuses.map((status, sIndex) => (
                                  <span key={sIndex} className={`status-badge ${status.toLowerCase().replace(/[^a-z]/g, '')}`}>
                                    {status}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="bill-statuses">
                      {Array.from(resource.billStatusTypes).map((status, sIndex) => (
                        <span key={sIndex} className={`status-badge ${status.toLowerCase().replace(/[^a-z]/g, '')}`}>
                          {status}
                        </span>
                      ))}
                    </td>
                    <td className="bill-allocation-total">
                      <span className={`allocation-value ${resource.totalBillAllocation > 100 ? 'over-allocated' : ''}`}>
                        {resource.totalBillAllocation.toFixed(0)}%
                      </span>
                    </td>
                    <td className="cost-allocation-total">
                      <span className={`allocation-value ${resource.totalCostAllocation > 100 ? 'over-allocated' : ''}`}>
                        {resource.totalCostAllocation.toFixed(0)}%
                      </span>
                    </td>
                    <td className="total-allocation">
                      <span className={`allocation-value ${resource.totalAllocation > 100 ? 'over-allocated' : ''}`}>
                        {resource.totalAllocation.toFixed(0)}%
                      </span>
                    </td>
                    <td className="status">
                      {resource.hasMultipleBillStatuses ? (
                        <span className="status-mixed">⚠️ Mixed</span>
                      ) : (
                        <span className="status-single">✅ Single</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <h3>📊 No multi-project resources found</h3>
            <p>
              {searchTerm 
                ? `No resources match your search "${searchTerm}" with the current filters.`
                : "No resources are currently assigned to multiple projects with the selected filter."
              }
            </p>
            {searchTerm && (
              <button 
                className="clear-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiProjectView;